export const LETTER_PAGE = Object.freeze({ width: 612, height: 792 });

export const FIELD_GROUPS = [
  {
    title: "Client",
    fields: [
      {
        label: "Client Signature",
        semanticKey: "client.signature",
        fieldType: "signature",
        width: 153,
        height: 40,
      },

      {
        label: "Client Email",
        semanticKey: "client.email",
        fieldType: "text",
        width: 180,
        height: 28,
      },
      {
        label: "Client Signed Date",
        semanticKey: "client.date",
        fieldType: "date",
        width: 100,
        height: 28,
      },
      { label: "Client First Name", semanticKey: "client.first_name", fieldType: "text" },
      { label: "Client Last Name", semanticKey: "client.last_name", fieldType: "text" },
      { label: "Client Birthday", semanticKey: "client.birthday", fieldType: "text" },
      { label: "Client SSN", semanticKey: "client.ssn", fieldType: "text" },
      {
        label: "Client Address 1",
        semanticKey: "client.address1",
        fieldType: "text",
        width: 190,
      },
      {
        label: "Client Address 2",
        semanticKey: "client.address2",
        fieldType: "text",
        width: 190,
      },
      { label: "Client City", semanticKey: "client.city", fieldType: "text" },
      {
        label: "Client State",
        semanticKey: "client.state",
        fieldType: "text",
        width: 90,
      },
      { label: "Client ZIP", semanticKey: "client.zip", fieldType: "text", width: 90 },
    ],
  },
  {
    title: "Defendant",
    fields: [
      {
        label: "Defendant Signature",
        semanticKey: "defendant.signature",
        fieldType: "signature",
        width: 153,
        height: 40,
      },
      {
        label: "Defendant Entity Name",
        semanticKey: "defendant.entity_name",
        fieldType: "text",
        width: 153,
        height: 28,
      },
      {
        label: "Defendant Email",
        semanticKey: "defendant.email",
        fieldType: "text",
        width: 180,
        height: 28,
      },
      {
        label: "Defendant Signed Date",
        semanticKey: "defendant.date",
        fieldType: "date",
        width: 100,
        height: 28,
      },
      { label: "Defendant First Name", semanticKey: "defendant.first_name", fieldType: "text" },
      { label: "Defendant Last Name", semanticKey: "defendant.last_name", fieldType: "text" },
    ],
  },
  {
    title: "Other Party",
    fields: [
      {
        label: "Party Signature",
        semanticKey: "party.signature",
        fieldType: "signature",
        width: 153,
        height: 40,
      },

      {
        label: "Party Email",
        semanticKey: "party.email",
        fieldType: "text",
        width: 180,
        height: 28,
      },
      {
        label: "Party Signed Date",
        semanticKey: "party.date",
        fieldType: "date",
        width: 100,
        height: 28,
      },
      { label: "Party First Name", semanticKey: "party.first_name", fieldType: "text" },
      { label: "Party Last Name", semanticKey: "party.last_name", fieldType: "text" },
    ],
  },
];

const getEmailOptions = (person = {}) => {
  const candidates = [
    { email: person.primary_email?.email, label: "primary" },
    { email: person.email, label: person.email_source || "primary" },
    ...(Array.isArray(person.emails) ? person.emails : []),
    { email: person.home_email, label: "home" },
    { email: person.home_contact?.email, label: "home" },
    { email: person.party_home_contact?.email, label: "home" },
    { email: person.work_contact?.email, label: "work" },
    { email: person.party_work_contact?.email, label: "work" },
  ];

  const seen = new Set();
  return candidates.reduce((emails, candidate) => {
    const email = typeof candidate === "string" ? candidate : candidate?.email;
    const normalizedEmail = email?.trim();
    if (!normalizedEmail || seen.has(normalizedEmail.toLowerCase())) return emails;
    seen.add(normalizedEmail.toLowerCase());
    emails.push({
      email: normalizedEmail,
      label: typeof candidate === "object" ? candidate.label || "" : "",
    });
    return emails;
  }, []);
};

export const normalizeRecipients = (payload = {}) => {
  const parties = payload.data || payload;
  const groups = [
    { type: "Client", role: "client", label: "Clients", items: parties.clients || [] },
    {
      type: "Defendant",
      role: "defendant",
      label: "Defendants",
      items: parties.defendants || [],
    },
    {
      type: "OtherParty",
      role: "otherparty",
      label: "Other Parties",
      items: parties.other_parties || [],
    },
  ];

  return groups.map((group) => ({
    ...group,
    items: group.items.map((person) => {
      const firstName = person.first_name || person.party_first_name || "";
      const lastName = person.last_name || person.party_last_name || "";
      const entityName = person.entity_name || "";
      const emails = getEmailOptions(person);
      return {
        id: person.id,
        key: `${group.type}:${person.id}`,
        type: group.type,
        role: group.role,
        firstName,
        lastName,
        entityName,
        isEntity: group.role === "defendant" && Boolean(entityName.trim()),
        email: emails[0]?.email || "",
        emails,
        displayName:
          entityName.trim() || `${firstName} ${lastName}`.trim() || `${group.type} ${person.id}`,
      };
    }),
  }));
};

export const requestErrorMessage = (error, fallback) => {
  const body = error?.response?.data;
  if (typeof body === "string") return body;
  if (typeof body?.error === "string") return body.error;
  if (typeof body?.detail === "string") return body.detail;
  if (typeof body?.message === "string") return body.message;
  if (body && typeof body === "object") {
    const value = Object.values(body).find(
      (item) => typeof item === "string" || Array.isArray(item)
    );
    if (Array.isArray(value)) return value.join(" ");
    if (typeof value === "string") return value;
  }
  return fallback;
};

export const defaultExpirationDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  return date.toISOString().slice(0, 10);
};
